import React, { useContext, useState, useEffect } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { genThemedStyled, Theme, ThemeContext } from '../../../utilities/theme';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import S from '../../../foundations/size';
import { FUTURO_TYPEFACES as F } from '../../../foundations/font';
import { Icon } from '../../../foundations/Icons';

export type CheckboxVariant = 'small' | 'large';

export type CheckboxProps = {
  /** The checkbox input's name */
  name?: string;
  children?: React.ReactNode;
  /** "small" or "large", the checkbox's size. by default has a variant of "large" */
  variant?: CheckboxVariant;
  /** Checked state of the checkbox */
  checked?: boolean;
  /** Indeterminate state of the checkbox. */
  indeterminate?: boolean;
  /** Disabled state of the checkbox */
  disabled?: boolean;
  /** Error state of the checkbox */
  hasError?: boolean;
  /** Checkbox label */
  label?: string | React.ReactNode;
  /** Hint text for checkbox */
  hint?: string;
  /** Error label and text for checkbox */
  errorLabel?: string;
  /** Event when the checkbox value changes */
  onChange?: (checked: HTMLInputElement['checked']) => void;
};

const getTheme = (
  theme: Theme,
  { hasError, disabled, indeterminate }: CheckboxProps,
  checked: boolean
): themeStyles => {
  const colors = colorsForTheme(theme);
  let borderColor;
  if (hasError) {
    borderColor = colors.ROSE;
  } else if (disabled) {
    borderColor = colors.ASH;
  } else {
    borderColor = colors.SILVER;
  }

  let backgroundColor;
  let disabledBackgroundColor;
  if (checked) {
    backgroundColor = colors.SWEET_ORANGE;
    disabledBackgroundColor = colors.SHADOW;
  } else if (indeterminate) {
    backgroundColor = colors.SWEET_ORANGE;
    disabledBackgroundColor = colors.SHADOW;
  } else {
    backgroundColor = colors.SWAN;
    disabledBackgroundColor = colors.SMOKE;
  }

  const themeObj = {
    backgroundColor: disabled ? disabledBackgroundColor : backgroundColor,
    borderColor,
    borderFocus: colors.SPACE,
    errorBorder: colors.ROSE,
    labelColor: colors.STORM,
    hintColor: colors.STEEL,
    errorLabelColor: colors.ROSE,
    disabledLabelColor: colors.STEEL,
    disabledHintColor: colors.SHADOW,
    iconColor: colors.SWAN,
  };
  return themeObj;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  borderColor: ColorCodes;
  borderFocus: ColorCodes;
  errorBorder: ColorCodes;
  labelColor: ColorCodes;
  hintColor: ColorCodes;
  errorLabelColor: ColorCodes;
  disabledLabelColor: ColorCodes;
  disabledHintColor: ColorCodes;
  iconColor: ColorCodes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const CheckboxInput = ThemeStyled.input<{
  variant?: CheckboxVariant;
  hasError?: boolean;
  indeterminate?: boolean;
  theme: themeStyles;
}>(({ variant, indeterminate, theme }) => ({
  cursor: 'pointer',
  tabSize: '4',
  borderCollapse: 'collapse',
  boxSizing: 'border-box',
  appearance: 'none',
  display: 'inline-block',
  verticalAlign: 'middle',
  backgroundOrigin: 'border-box',
  userSelect: 'none',
  flexShrink: 0,
  width: variant === 'small' ? '1.25rem' : '1.5rem',
  height: variant === 'small' ? '1.25rem' : '1.5rem',
  backgroundColor: theme.backgroundColor,
  border: `0.125rem solid ${indeterminate ? 'transparent' : theme.borderColor}`,
  borderRadius: S.XXXS,
  marginTop: S.XXXS,

  '&:focus': {
    borderColor: 'transparent',
    outline: `0.125rem solid ${theme.borderFocus}`,
  },

  '&:checked': {
    borderColor: 'transparent',
  },
}));

const TextWrapper = ThemeStyled.div<{
  hasHint?: string;
}>(({ hasHint }) => ({
  gap: S.XXXS,
  paddingTop: S.XXXS,
  paddingBottom: S.XXXS,
  paddingLeft: S.XS,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  userSelect: 'none',
}));

const LabelWrapper = ThemeStyled.label<{
  disabled?: boolean;
  theme: themeStyles;
}>(
  ({ disabled, theme }) => ({
    color: disabled ? theme.disabledLabelColor : theme.labelColor,
    paddingBottom: S.XXXS,
    width: '100%',
  }),
  F.BODY
);

const HintWrapper = ThemeStyled.label<{
  disabled?: boolean;
  theme: themeStyles;
}>(
  ({ disabled, theme }) => ({
    color: disabled ? theme.disabledHintColor : theme.hintColor,
  }),
  F.H6
);

const ErrorLabelWrapper = ThemeStyled.label(
  p => ({
    color: p.theme.errorLabelColor,
  }),
  F.H6
);

const CheckboxWrapper = ThemeStyled.div({
  display: 'flex',
  flexDirection: 'row',
  position: 'relative',
});

const CheckboxIconWrapper = ThemeStyled.div<{
  variant?: CheckboxVariant;
  theme: themeStyles;
}>(({ variant, theme }) => ({
  pointerEvents: 'none',
  position: 'absolute',
  top: S.XXXS, // match CheckboxInput marginTop
  left: 0,
  width: variant === 'small' ? '1.25rem' : '1.5rem',
  height: variant === 'small' ? '1.25rem' : '1.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.iconColor,
}));

const CheckboxIcon = variant => {
  let icon;
  switch (variant.variant) {
    case 'indeterminate':
      icon = 'mMinus';
      break;
    case 'checked':
    default:
      icon = 'sCheck';
  }
  return <Icon name={icon} inheritFill />;
};

/**
 * A futuro checkebox
 *
 * @argument name string - optional prop. the checkbox input's name
 * @argument variant string - optional prop. "small" or "large", the checkbox's size. by default has a variant of "large"
 * @argument checked boolean - optional prop. checked state of the checkbox
 * @argument indeterminate boolean - optional prop. indeterminate state of the checkbox.
 * @argument disabled boolean - optional prop. checkbox is disabled (readonly)
 * @argument hasError boolean - optional prop. error state of the checkbox
 * @argument label string - optional prop. checkbox label
 * @argument hint string - optional prop. hint text for checkbox
 * @argument errorLabel string - optional prop. error label and text for checkbox
 * @argument onChange - optional prop. event when the checkbox value changes
 *
 * Note: checked and indeterminate props cannot be true at the same time
 *
 * Based off the designs in the figma file:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=31%3A370
 *
 * @example
 *
 * <Checkbox
 *  name='checkbox'
 *  variant='small'
 *  checked={true}
 *  label='Checkbox 1'
 *  hint='This is checkbox 1'
 * />
 */

export const Checkbox = (props: CheckboxProps) => {
  const {
    name,
    children,
    variant,
    checked = false,
    indeterminate = false,
    disabled = false,
    hasError = false,
    label,
    hint,
    errorLabel,
    onChange,
  } = props;

  const [checkboxValue, setCheckboxValue] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxValue(!checkboxValue);
    if (onChange) {
      onChange(event.target.checked);
    }
  };

  useEffect(() => {
    setCheckboxValue(checked);
  }, [checked]);

  return (
    <ThemeProvider
      theme={getTheme(useContext(ThemeContext), { ...props }, checkboxValue)}
    >
      <CheckboxWrapper>
        <CheckboxInput
          aria-label={name}
          type="checkbox"
          key={checkboxValue.toString()}
          name={name}
          id={name}
          variant={variant}
          checked={checkboxValue}
          indeterminate={indeterminate}
          hasError={hasError}
          disabled={disabled}
          onChange={handleChange}
        />
        {checkboxValue ? (
          <CheckboxIconWrapper variant={variant}>
            <CheckboxIcon variant="checked" />
          </CheckboxIconWrapper>
        ) : (
          indeterminate && (
            <CheckboxIconWrapper variant={variant}>
              <CheckboxIcon variant="indeterminate" />
            </CheckboxIconWrapper>
          )
        )}
        {(label || children) && (
          <TextWrapper hasHint={hint}>
            {label && (
              <LabelWrapper htmlFor={name} disabled={disabled}>
                {label}
              </LabelWrapper>
            )}
            {children && <LabelWrapper htmlFor={name}>{children}</LabelWrapper>}
            {hint && (
              <HintWrapper htmlFor={name} disabled={disabled}>
                {hint}
              </HintWrapper>
            )}
            {hasError && <ErrorLabelWrapper>{errorLabel}</ErrorLabelWrapper>}
          </TextWrapper>
        )}
      </CheckboxWrapper>
    </ThemeProvider>
  );
};

export default Checkbox;
